import React, { Fragment, useEffect, useState } from "react";
import usePersistentFilters from "../Common/usePersistentFilters";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Card, Table, Button, Form, Badge } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import PermissionWrapper from "../PermissionWrapper";
import api from "../../../api";
import swal from "sweetalert";
import { useSelector } from "react-redux";

const WarehouseList = () => {
 const navigate = useNavigate();
 const user = useSelector((state) => state.auth?.userDetails || {});
 const [warehouses, setWarehouses] = useState([]);
 const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalCount: 0 });
 const [filters, setFilters] = usePersistentFilters("warehouse_filters", { search: "", status: "", page: 1, limit: 10 });
 const [loading, setLoading] = useState(false);

 useEffect(() => {
 fetchWarehouses();
 }, [filters.page, filters.limit, filters.status]);

 const fetchWarehouses = async () => {
 try {
 setLoading(true);
 const queryParams = new URLSearchParams();
 if (filters.search) queryParams.append("search", filters.search);
 if (filters.status) queryParams.append("status", filters.status);
 queryParams.append("page", filters.page);
 queryParams.append("limit", filters.limit);

 const res = await api.get(`/warehouses?${queryParams.toString()}`);
 if (res.data.success) {
 setWarehouses(res.data.data || []);
 setPagination(res.data.pagination || pagination);
 }
 } catch (error) {
 swal("Error", "Failed to load warehouses", "error");
 } finally {
 setLoading(false);
 }
 };

 const handleSearch = () => {
 setFilters((prev) => ({ ...prev, page: 1 }));
 fetchWarehouses();
 };

 const handleDelete = async (id) => {
 const ok = await swal({
 title: "Delete warehouse?",
 text: "This warehouse will be removed permanently.",
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });
 if (!ok) return;

 try {
 const res = await api.delete(`/warehouses/${id}`);
 if (res.data.success) {
 swal("Deleted", "Warehouse deleted successfully", "success");
 fetchWarehouses();
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || "Failed to delete warehouse", "error");
 }
 };

 return (
 <Fragment>
 <PageTitle activeMenu="Warehouses" motherMenu="Inventory" />
 <Row>
 <Col lg={12}>
 <Card>
 <Card.Header className="d-flex justify-content-between align-items-center">
 <Card.Title className="mb-0">Warehouse List</Card.Title>
 <div className="d-flex gap-2">
 <Button variant="secondary" as={Link} to="/inventory-dashboard">Back</Button>
 <PermissionWrapper user={user} required={["create:inventory"]}>
 <Button as={Link} to="/warehouses/add">+ Add Warehouse</Button>
 </PermissionWrapper>
 </div>
 </Card.Header>
 <Card.Body>
 <Row className="g-2 mb-3">
 <Col md={5}>
 <Form.Control
 placeholder="Search warehouse"
 value={filters.search}
 onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
 onKeyDown={(e) => e.key === "Enter" && handleSearch()}
 />
 </Col>
 <Col md={3}>
 <Form.Select
 value={filters.status}
 onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))}
 >
 <option value="">All Status</option>
 <option value="Active">Active</option>
 <option value="Inactive">Inactive</option>
 </Form.Select>
 </Col>
 <Col md={2}>
 <Button className="w-100" onClick={handleSearch}>Search</Button>
 </Col>
 </Row>

 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th style={{ textAlign: "center" }}>Name</th>
 <th style={{ textAlign: "center" }}>Code</th>
 <th style={{ textAlign: "center" }}>Location</th>
 <th style={{ textAlign: "center" }}>Contact</th>
 <th style={{ textAlign: "center" }}>Status</th>
 <th style={{ textAlign: "center" }}>Default</th>
 <th style={{ textAlign: "center" }}>Actions</th>
 </tr>
 </thead>
 <tbody>
 {!loading && warehouses.map((wh) => (
 <tr key={wh._id}>
 <td style={{ textAlign: "center" }}>{wh.warehouseName}</td>
 <td style={{ textAlign: "center" }}>{wh.warehouseCode}</td>
 <td style={{ textAlign: "center" }}>{[wh.city, wh.state].filter(Boolean).join(", ") || "-"}</td>
 <td style={{ textAlign: "center" }}>{wh.contactPerson || "-"}</td>
 <td style={{ textAlign: "center" }}>
 <Badge bg={wh.status === "Active" ? "success" : "secondary"}>{wh.status}</Badge>
 </td>
 <td style={{ textAlign: "center" }}>{wh.isDefault ? <Badge bg="info">Yes</Badge> : "-"}</td>
 <td>
 <div className="d-flex justify-content-center">
 <PermissionWrapper user={user} required={["edit:inventory"]}>
 <Button size="sm" variant="outline-primary" className="me-2" onClick={() => navigate(`/warehouses/edit/${wh._id}`)}>Edit</Button>
 <Button size="sm" variant="outline-danger" onClick={() => handleDelete(wh._id)}>Delete</Button>
 </PermissionWrapper>
 </div>
 </td>
 </tr>
 ))}
 {!loading && warehouses.length === 0 && (
 <tr>
 <td colSpan="7" className="text-center text-muted py-4">No warehouses found</td>
 </tr>
 )}
 </tbody>
 </Table>
 </div>

 <AppPagination
 currentPage={pagination.currentPage}
 totalPages={pagination.totalPages}
 totalCount={pagination.totalCount}
 itemsLength={warehouses.length}
 limit={filters.limit}
 onPageChange={(page) => setFilters((prev) => ({ ...prev, page }))}
 />
 </Card.Body>
 </Card>
 </Col>
 </Row>
 </Fragment>
 );
};

export default WarehouseList;
